import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { adminConfig } from 'src/environments/environment';
import { IPrepLocation } from 'src/app/common/models/prep-location';
import { Operation } from 'src/app/common/models/operation';
import { AdminUIHelpers } from '../common/helpers';
import { IPrepLocationHoursOverride } from '../common/models/prep-location-hours-override';
import { MultiPrepSelect } from '../common/models/multiPrepSelect';
import { PatchPrepLocationsRequest } from '../common/models/patchPrepLocationsRequest';
import { IBulkHoursOverride } from '../common/models/bulkhoursoverride';

@Injectable({
  providedIn: 'root',
})
export class PrepLocationsService {
  private baseUriV2: string = adminConfig.baseAdminApiUri;

  constructor(private http: HttpClient) {}

  public getPrepLocations(facilityId: string): Observable<IPrepLocation[]> {
    return this.http
      .get<IPrepLocation[]>(this.baseUriV2 + '/api/organizations/1/facilities/' + facilityId + '/preplocations')
      .pipe(
        map((data: IPrepLocation[]) => {
          data = AdminUIHelpers.sortPrepLocationsByName(data);
          return AdminUIHelpers.convertPrepLocationFieldsToMoments(data);
        }),
      );
  }

  public getPrepLocationHoursOverrides(facilityId: string): Observable<IPrepLocationHoursOverride[]> {
    return this.http.get<IPrepLocationHoursOverride[]>(
      this.baseUriV2 + '/api/organizations/1/facilities/' + facilityId + '/preplocations/hoursoverrides',
    );
  }

  public updatePrepLocation(
    facilityId: string,
    prepLocationTypeName: string,
    operations: Operation[],
  ): Observable<any> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this.http.patch(
      this.baseUriV2 + '/api/organizations/1/facilities/' + facilityId + '/preplocations/' + prepLocationTypeName,
      JSON.stringify(operations),
      options,
    );
  }

  public updatePrepLocations(facilityId: string, bulkPatchResource: PatchPrepLocationsRequest): Observable<any> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this.http.patch(
      this.baseUriV2 + '/api/organizations/1/facilities/' + facilityId + '/preplocations/',
      JSON.stringify(bulkPatchResource),
      options,
    );
  }

  public upsertHoursOverrides(hoursOverride: IPrepLocationHoursOverride[]): Observable<Object> {
    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
    };

    return this.http
      .put<IPrepLocationHoursOverride[]>(
        this.baseUriV2 + '/api/bulkupsert/organizations/facilities/preplocations/hoursoverrides',
        JSON.stringify(hoursOverride),
        options,
      )
      .pipe(catchError(this.handleError));
  }

  public getDurableStatus(instanceId: string, code: string): Observable<HttpResponse<Object>> {
    return this.http.get(
      this.baseUriV2
        + '/api/bulkupsert/organizations/facilities/preplocations/hoursoverrides/status/'
        + instanceId
        + '/'
        + code,
      { observe: 'response' },
    );
  }

  public upsertBulkHoursOverrides(hoursOverride: IBulkHoursOverride[]): Observable<Object> {
    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
    };

    return this.http
      .put<IPrepLocationHoursOverride[]>(
        this.baseUriV2 + '/api/bulkupsert/organizations/facilities/preplocations/hoursoverrides',
        JSON.stringify(hoursOverride),
        options,
      )
      .pipe(catchError(this.handleError));
  }

  public deleteHoursOverride(hoursOverride: IPrepLocationHoursOverride): Observable<Object> {
    return this.http
      .delete(
        this.baseUriV2
          + '/api/organizations/'
          + hoursOverride.organizationId
          + '/facilities/'
          + hoursOverride.facilityId
          + '/preplocations/'
          + hoursOverride.prepLocationTypeName
          + '/hoursoverrides?&date='
          + hoursOverride.date,
      )
      .pipe(catchError(this.handleError));
  }

  public getSelectedPrepLocations(prepLocations: MultiPrepSelect[], currentPrepLocation: string): string[] {
    const prepLocationsList = prepLocations
      .filter((p) => {
        return p.isSelected === true;
      })
      .map((p) => {
        return p.prepLocationName;
      });

    return prepLocationsList.length > 0 ? prepLocationsList : [currentPrepLocation];
  }

  private handleError(err: HttpErrorResponse): Observable<never> {
    return throwError(() => err.message);
  }
}
